import { encounterCreatureField, itemsCol, layerValue, statusIdOf } from './array-types';
import { creaturePictureField, selectCreaturePicture } from './creature-token-geometry';
import { statusItemId, statusRows } from '../janus-engine/statuses/expiry';
import { readPlayerCharacterField } from '../janus-engine/display/player-character-field';
import { captureCommandRollVfx, type RollVfxTypes } from '../janus-engine/presentation/roll-vfx-cue';
import { normalizePhpListish } from './php-listish';
import { ftCurrentTruthSink } from './truth-sink';

type Row = Record<string, unknown>;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const upper = (value: unknown): string => String(value ?? '').toUpperCase();

/** Presentation captured by the page before its decision changes the participant. No rules run in the log writer. */
export function logParticipantSnapshot(row: Row): Row {
    return {
        name: String(encounterCreatureField(row.encounter_creatures, 'encounter_creature_name') || readPlayerCharacterField(row, 'player_name') || itemsCol(row, 'item_name') || ''),
        picture_url: creaturePictureField(selectCreaturePicture(row), 'pic_url') ?? null,
        statuses: statusRows(row, 'target').map(status => ({
            status_id: statusIdOf(status), status_item_id: statusItemId(status),
            status_stack: layerValue(status.status_stack) ?? 1,
        })),
    };
}

/** Find only command participants, preferring each actor's own cache over an encounter projection. */
export function withLogParticipants<T extends object>(command: T, roots?: readonly unknown[]): T & { log_participants: Record<string, Row> } {
    if (!Object.isExtensible(command)) return withLogParticipants({ ...command }, roots);
    const cache = roots ?? (() => {
        const ft = (window as unknown as { FateTide?: {
            casterCache?: Map<string, unknown>; objectCache?: Map<string, unknown>; encounterCache?: Map<string, unknown>;
        } }).FateTide;
        return [...(ft?.casterCache?.values() ?? []), ...(ft?.objectCache?.values() ?? []), ...(ft?.encounterCache?.values() ?? [])];
    })();
    const types = typeof window === 'undefined' ? {} : (window as unknown as { ROLLTYPE_CATALOG?: RollVfxTypes }).ROLLTYPE_CATALOG ?? {};
    const encounterId = upper((command as Row).encounter_id);
    const base = ftCurrentTruthSink()?.heldBase?.(encounterId)
        ?? (typeof window === 'undefined' ? undefined : window.ftReadHeldEntityBase?.(encounterId, 'encounter')?.base);
    const vfxRows = normalizePhpListish(record(record(base?.inventory_metadata)?.definitionAssets)?.vfxRows).filter(
        (value): value is Row => record(value) !== null);
    captureCommandRollVfx(command as Row, id => {
        for (const value of cache) {
            const root = record(value);
            if (!root) continue;
            const definition = record(record(root.catalog)?.[upper(id)]);
            if (definition) return definition;
            if (upper(itemsCol(root, 'item_id')) === upper(id)) return root;
        }
        for (const value of cache) for (const member of normalizePhpListish(record(value)?.encounter_creatures_arr)) {
            const definition = record(record(record(member)?.catalog)?.[upper(id)]);
            if (definition) return definition;
        }
        return undefined;
    }, types, vfxRows);
    if ('log_participants' in command) return command as T & { log_participants: Record<string, Row> };
    const wanted = new Set<string>();
    const visit = (value: unknown): void => {
        if (typeof value === 'string' && /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value)) wanted.add(upper(value));
        else if (value !== null && typeof value === 'object') for (const entry of Object.values(value)) visit(entry);
    };
    visit(command);
    const participants: Record<string, Row> = {};
    const add = (value: unknown): void => {
        const row = record(value);
        if (!row) return;
        const id = upper(encounterCreatureField(row.encounter_creatures, 'encounter_creature_id') || readPlayerCharacterField(row, 'item_id') || itemsCol(row, 'item_id'));
        if (wanted.has(id) && !participants[id] && (record(row.encounter_creatures) || record(row.player_characters))) participants[id] = logParticipantSnapshot(row);
    };
    for (const value of cache) add(value);
    for (const value of cache) {
        const row = record(value);
        for (const member of Array.isArray(row?.encounter_creatures_arr) ? row.encounter_creatures_arr : []) add(member);
    }
    return Object.assign(command, { log_participants: participants });
}
